/**
 * IMPORTS
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { CircleOff, MessageCircleQuestion, Receipt, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import { Button } from "@components/button/button";
import { ExcludeItem } from "@components/excluir-item";
import { type ObservacaoFormData, ObservacaoFormModal } from "@components/observacao-form-modal";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";
import { DynamicTable } from "@components/table-custom/table";
import { Tabs, TabsContent } from "@components/tabs";

import { SearchInputVendaCreditos } from "@pages/admin/gerenciar-clientes/components/search-venda-creditos";

import { useListarProdutosAtivosSelecao } from "@/hooks/produtos/listar-produtos-ativos-selecao";
import { useVendasCreditosPorCliente } from "@/hooks/vendas-creditos";
import { useAtualizarVendaCredito } from "@/hooks/vendas-creditos/atualizar-uma-venda";
import { useCancelarVendaCreditos } from "@/hooks/vendas-creditos/cancelar-venda";
import { useRealizarCompraCreditos } from "@/hooks/vendas-creditos/criar-venda";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";
import { IVendaCredito } from "@/store-zustand/vendas-creditos";
import { useStoreZustandVendasCreditos } from "@/store-zustand/vendas-creditos/credits";

import { FEATURES, useFeature } from "@/utils/auth/features";

const schema = z.object({
  type: z.enum(["Entrada", "Saída"], {
    errorMap: () => ({ message: "Selecione o tipo da movimentação" }),
  }),
  produto: z.string().min(1, "Selecione um produto"),
  quantity: z.coerce.number().min(1, "Informe uma quantidade válida"),
  observacao: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const dataTitleTableVendasCreditos = [
  { label: "Produto", accessor: "nome_produto", limit: 60 },
  {
    label: "Transação",
    accessor: "tipo_transacao",
    isBadge: true,
    badgeClassName: "bg-red-500",
  },
  {
    label: "Situação",
    accessor: "status",
    isBadge: true,
    badgeClassName: "bg-red-500",
  },
  {
    label: "Créditos",
    accessor: "saldo_creditos",
    isBadge: true,
    badgeClassName: "bg-green-500",
  },
  { label: "Responsável", accessor: "designer", limit: 60 },

  { label: "Data", accessor: "data" },
];

type IAddCreditToClienteModalProps = {
  handleToggleCreateActvityModal: () => void;
  userIdCliente: number;
};

const AddCreditToClienteModal = ({
  handleToggleCreateActvityModal,
  userIdCliente,
}: IAddCreditToClienteModalProps) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });
  const { user } = useStoreZustandUserAuth();

  const podeVerExtratoCredito = useFeature(FEATURES.VISUALIZAR_EXTRATO_CREDITO);

  const { criarVendaCreditosAsync, isLoading } = useRealizarCompraCreditos();
  const {
    currentPage,
    setCurrentPage,
    totalItemsPage,
    itemsPerPage,
    lastPage,
    vendaCredito,
    setVendaCredito,
    isModalObservacao,
    setIsModalObservacao,
    isModalExcluirVendaCredito,
    setIsModalExcluirVendaCredito,
  } = useStoreZustandVendasCreditos();
  const {
    vendasCreditos,
    isLoading: isLoadingPage,
    paginations,
  } = useVendasCreditosPorCliente(userIdCliente);

  const { produtos } = useListarProdutosAtivosSelecao();
  const { atualizarVendaCreditoAsync, isLoading: isLoadingAtualizarVendaCredito } =
    useAtualizarVendaCredito();

  const { cancelarVendaAsync, isLoadingCancelarVenda } = useCancelarVendaCreditos();

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!user?.id) return;

      const produtoSelecionado = produtos?.find((item) => item.nome === data.produto);

      if (!produtoSelecionado) {
        toast.error("Selecione um produto válido.");
        return;
      }

      const payload = {
        cliente_id: userIdCliente,
        user_id_executor: user?.id,
        produto_id: produtoSelecionado.id,
        tipo_transacao: data.type === "Saída" ? "saida" : "entrada",
        status: "pendente",
        valor: 0,
        valor_total: 0,
        quantidade_creditos: data.quantity,
        observacao: data.observacao,
      };

      await criarVendaCreditosAsync(payload);
      handleToggleCreateActvityModal();
    },
    [userIdCliente, criarVendaCreditosAsync, user, produtos, handleToggleCreateActvityModal]
  );
  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? currentPage + 1
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, setCurrentPage]
  );

  const onSubmitUpdate = useCallback(
    (data: ObservacaoFormData) => {
      if (!user?.id) {
        toast.error("Usuário não autenticado.");
        return;
      }

      atualizarVendaCreditoAsync(
        {
          venda_credito_id: Number(vendaCredito?.id),
          payload: {
            observacao: data.observation || "N/A",
          },
        },
        {
          onSuccess: () => {
            setIsModalObservacao(!isModalObservacao);
            const update = {
              ...vendaCredito,
              observacao: data.observation!,
            };
            setVendaCredito(update as any);
          },
          onError: () => {
            setIsModalObservacao(!isModalObservacao);
          },
        }
      );
    },
    [
      atualizarVendaCreditoAsync,
      user,
      vendaCredito,
      setVendaCredito,
      isModalObservacao,
      setIsModalObservacao,
    ]
  );

  const onSubmitCancelVendaCredito = useCallback(async () => {
    if (!user?.id) {
      toast.error("Usuário não autenticado.");
      return;
    }

    await cancelarVendaAsync(Number(vendaCredito?.id), {
      onSuccess: () => {
        const update = {
          ...vendaCredito,
          status: "cancelado"!,
        };
        setIsModalExcluirVendaCredito(!isModalExcluirVendaCredito);
        setVendaCredito(update as any);
      },
      onError: () => {
        setIsModalExcluirVendaCredito(!isModalExcluirVendaCredito);
      },
    });
  }, [
    user,
    cancelarVendaAsync,
    setIsModalExcluirVendaCredito,
    setVendaCredito,
    vendaCredito,
    isModalExcluirVendaCredito,
  ]);

  const navegarParaExtratoCreditoCliente = useCallback(() => {
    handleToggleCreateActvityModal();
    navigate("/edit-client-data", {
      state: {
        cliente: { id: userIdCliente },
        abaInicial: "extrato-movimentacoes-credito",
      },
    });
  }, [handleToggleCreateActvityModal, navigate, userIdCliente]);

  return (
    <Tabs defaultValue="all">
      <div className="fixed inset-0 flex items-center justify-center bg-black/60 p-4">
        <div className="h-[80vh] w-[80vw] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Realizar movimentação</h2>
            <button onClick={handleToggleCreateActvityModal}>
              <X className="size-5 text-gray-500 hover:text-gray-700" />
            </button>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              {/* Tipo */}
              <div>
                <label className="mb-1 block text-gray-700">Tipo</label>
                <select
                  {...register("type")}
                  className="w-full rounded-md border px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Entrada">Entrada</option>
                  <option value="Saída">Saída</option>
                </select>
                {errors.type && <p className="text-sm text-red-500">{errors.type.message}</p>}
              </div>

              {/* Produto */}
              <div>
                <p className="mb-1 block text-gray-700">Produto</p>
                <select
                  {...register("produto")}
                  className="w-full rounded-md border px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione o produto</option>
                  {produtos.map((produto) => (
                    <option key={produto.id} value={produto.nome}>
                      {produto.nome}
                    </option>
                  ))}
                </select>
                {errors.produto && <p className="text-sm text-red-500">{errors.produto.message}</p>}
              </div>

              {/* Quantidade */}
              <div>
                <label className="mb-1 block text-gray-700">Quantidade</label>
                <input
                  type="number"
                  {...register("quantity")}
                  placeholder="Informe a quantidade"
                  className="w-full rounded-md border px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.quantity && (
                  <p className="text-sm text-red-500">{errors.quantity.message}</p>
                )}
              </div>
            </div>
            {/* Observações */}
            <div>
              <p className="mb-2 text-zinc-800">Observações</p>
              <textarea
                {...register("observacao")}
                placeholder="Digite informações sobre a compra"
                className="h-20 w-full rounded-lg border border-zinc-800 px-4 pt-4 text-zinc-800 outline-none"
              />
            </div>

            <Button disabled={isLoading} type="submit" size="sm" className="h-8 w-full gap-1">
              {isLoading ? <SpinnerSimple /> : "  Salvar movimentação"}
            </Button>
          </form>

          {/* CRÉDITOS */}
          <TabsContent value="all">
            <DynamicTable
              title="Créditos"
              description="Histórico de movimentações"
              isLoadingPage={isLoadingPage}
              data={vendasCreditos}
              totalItems={totalItemsPage}
              itemsPerPage={itemsPerPage}
              columns={dataTitleTableVendasCreditos}
              onGoToPage={onGoToPage}
              paginations={paginations}
              currentPage={currentPage}
              lastPage={lastPage}
              actions={[
                {
                  icon: MessageCircleQuestion,
                  label: "Observação",
                  iconClassName: (item: any) =>
                    item.hasMensagem ? "h-4 w-4 text-yellow-500" : "h-4 w-4 text-zinc-900",
                  onClick: (item: IVendaCredito | null) => {
                    setVendaCredito(item);
                    setIsModalObservacao(!isModalObservacao);
                  },
                },
                {
                  icon: CircleOff,
                  label: "Cancelar venda",
                  textColor: "text-red-600",
                  onClick: (item: IVendaCredito | null) => {
                    setVendaCredito(item);
                    setIsModalExcluirVendaCredito(!isModalExcluirVendaCredito);
                  },
                },
              ]}
              childerSearch={
                podeVerExtratoCredito ? (
                  <div className="flex w-full max-w-full flex-wrap items-center justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 gap-1.5"
                      onClick={navegarParaExtratoCreditoCliente}
                    >
                      <Receipt className="h-4 w-4" />
                      Extrato de créditos
                    </Button>
                    <SearchInputVendaCreditos />
                  </div>
                ) : undefined
              }
            />
          </TabsContent>
        </div>

        {isModalObservacao && vendaCredito !== null && (
          <ObservacaoFormModal
            title="Adicionar Observação"
            submitLabel="Adicionar"
            initialObservacao={String(vendaCredito?.observacao ?? "")}
            isSubmitting={isLoadingAtualizarVendaCredito}
            textareaDisabled={
              vendaCredito?.status === "confirmado" || vendaCredito?.status === "cancelada"
            }
            submitDisabled={
              vendaCredito?.status === "confirmado" || vendaCredito?.status === "cancelada"
            }
            onClose={() => setIsModalObservacao(!isModalObservacao)}
            onSubmit={onSubmitUpdate}
          />
        )}

        {isModalExcluirVendaCredito && vendaCredito !== null && (
          <ExcludeItem
            isPending={isLoadingCancelarVenda}
            titleModal="Confirmar exclusão da venda"
            subTitleModal="Você tem certeza que deseja excluir esta venda do"
            descModal="Essa ação é irreversível e não poderá mais ser alterada. "
            data={{
              nome: vendaCredito?.nome_produto,
              situacao: vendaCredito?.status,
            }}
            handleOnSubmit={onSubmitCancelVendaCredito}
            handleToggleCreateActvityModal={() => {
              setIsModalExcluirVendaCredito(!isModalExcluirVendaCredito);
            }}
          />
        )}
      </div>
    </Tabs>
  );
};

export { AddCreditToClienteModal };
